// Návrat na základnu — „Noční kokpit", stejný vzor jako detail zastávky:
// waiting (jedu) → started → arrived (pečetě bedny volitelně) → done.
// Logika (stopProgressStore s id "depot", addBoxSeals) beze změny.

import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IonContent, IonPage, IonSpinner, useIonAlert } from "@ionic/react";

import { normalizeSeal } from "@routera/shared";
import { api } from "../lib/api";
import { openNavigation } from "../lib/navigation";
import { useStopProgressStore } from "../store/stopProgressStore";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { ThemeToggle } from "../components/ThemeToggle";

const DEPOT_ID = "depot";

const NavArrow = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2l7 19-7-4-7 4 7-19z" /></svg>
);

const ScanIcon = () => (
  <svg style={{ width: 19, height: 19, verticalAlign: -4 }} viewBox="0 0 24 24">
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="14" r="3.4" />
  </svg>
);

export function DepotScreen() {
  const history = useHistory();
  const location = useLocation();
  const rid = new URLSearchParams(location.search).get("rid");

  const [presentAlert] = useIonAlert();
  const [acting, setActing] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [boxSeals, setBoxSeals] = useState<string[]>([]);
  const [sealInput, setSealInput] = useState("");
  const [boxSaving, setBoxSaving] = useState(false);

  const startStop = useStopProgressStore((s) => s.startStop);
  const arriveStop = useStopProgressStore((s) => s.arriveStop);
  const pickup = useStopProgressStore((s) => s.pickup);
  const statusOf = useStopProgressStore((s) => s.statusOf);

  const { data: routes = [], isLoading } = useQuery({
    queryKey: ["mobile", "route", "today"],
    queryFn: api.route.today,
  });

  const route = rid ? (routes.find((r) => r.route_id === rid) ?? routes[0]) : routes[0];
  const backUrl = rid ? `/route?rid=${rid}` : "/route";

  const depotName = route ? `C.I.T ${route.depot}` : "Základna";
  const lat = (route as any)?.depot_lat ?? null;
  const lng = (route as any)?.depot_lng ?? null;

  const status = statusOf(DEPOT_ID);

  const wrapAction = async (fn: () => Promise<void>) => {
    setActing(true);
    try {
      await fn();
    } catch (e: any) {
      presentAlert({ header: "Chyba", message: e?.message ?? "Akce se nepovedla", buttons: ["OK"] });
    } finally {
      setActing(false);
    }
  };

  const handleScan = useCallback((code: string) => {
    setBoxSeals((prev) => prev.includes(code) ? prev : [...prev, code]);
  }, []);

  const handleAddSeal = () => {
    const code = normalizeSeal(sealInput);
    if (!code) return;
    setBoxSeals((prev) => prev.includes(code) ? prev : [...prev, code]);
    setSealInput("");
  };

  const handleComplete = () => wrapAction(async () => {
    if (boxSeals.length > 0) {
      setBoxSaving(true);
      try {
        await api.shift.addBoxSeals(boxSeals);
      } finally {
        setBoxSaving(false);
      }
    }
    await pickup(DEPOT_ID, boxSeals.length, boxSeals, { name: depotName });
  });

  if (isLoading) {
    return (
      <IonPage>
        <IonContent scrollY={false}>
          <div className="ck scenebg" style={{ height: "100%" }}>
            <div style={{ textAlign: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const preState = status === "waiting" || status === "started";

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.replace(backUrl)}>‹</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 19, display: "block" }}>{depotName.toUpperCase()}</b>
              <span style={{ display: "block", fontSize: 13.5, color: "var(--soft)" }}>Návrat na základnu</span>
            </div>
            <ThemeToggle />
          </div>

          <div className="stopbody">
            {status === "done" && (
              <div className="pad-x" style={{ marginTop: 12 }}>
                <div className="card" style={{ borderColor: "rgba(61,214,132,.5)", background: "var(--ok-dim)" }}>
                  <b style={{ color: "var(--ok)" }}>✓ Návrat dokončen</b>
                </div>
              </div>
            )}

            {preState && lat != null && (
              <div className="navrow">
                <button className="navbtn" style={{ flex: 1 }} onClick={() => openNavigation({ lat, lng, label: depotName }, "mapy")}>
                  <NavArrow /> Mapy.cz
                </button>
                <button className="navbtn" style={{ flex: 1 }} onClick={() => openNavigation({ lat, lng, label: depotName }, "waze")}>
                  <NavArrow /> Waze
                </button>
              </div>
            )}

            {status === "arrived" && (
              <>
                <div className="seals">
                  <div className="cap" style={{ display: "flex", alignItems: "baseline" }}>
                    Pečetě bedny · volitelné
                    <span className="spacer" />
                    <span className="mono" style={{ fontSize: 15, color: "var(--ink)", letterSpacing: 0 }}>
                      {boxSeals.length}
                    </span>
                  </div>
                  {boxSeals.map((code) => (
                    <div key={code} className="seal ok" style={{ display: "flex" }}>
                      <span className="code">{code}</span>
                      <span className="st">✓ naskenováno</span>
                      <button
                        onClick={() => setBoxSeals((prev) => prev.filter((c) => c !== code))}
                        aria-label="Smazat pečeť"
                        style={{ color: "var(--dim)", fontSize: 24, padding: "0 4px", lineHeight: 1 }}
                      >×</button>
                    </div>
                  ))}
                </div>
                <div style={{ padding: "12px 18px 0" }}>
                  <button className="scanbtn" onClick={() => setScannerOpen(true)}>
                    <ScanIcon /> &nbsp;Skenovat pečeť
                  </button>
                </div>

                {/* ruční zadání — plná klávesnice, pečetě mají i písmena */}
                <div className="tkrow" style={{ padding: "10px 18px 0", marginBottom: 0 }}>
                  <div className="tkfield">
                    <input
                      value={sealInput}
                      onChange={(e) => setSealInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddSeal(); }}
                      placeholder="Pečeť ručně…"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                  <button
                    className="navbtn"
                    style={{ flex: "none", width: 54, opacity: sealInput.trim() ? 1 : 0.5 }}
                    disabled={!sealInput.trim()}
                    onClick={handleAddSeal}
                  >＋</button>
                </div>
              </>
            )}
          </div>

          <div className="bottom">
            {status === "waiting" && (
              <button className={`cta${acting ? " locked" : ""}`} disabled={acting}
                onClick={() => void wrapAction(() => startStop(DEPOT_ID))}>
                Jedu na základnu
              </button>
            )}
            {status === "started" && (
              <button className={`cta${acting ? " locked" : ""}`} disabled={acting}
                onClick={() => void wrapAction(() => arriveStop(DEPOT_ID))}>
                Jsem na základně
              </button>
            )}
            {status === "arrived" && (
              <button className={`cta${(acting || boxSaving) ? " locked" : ""}`} disabled={acting || boxSaving}
                onClick={() => void handleComplete()}>
                {boxSaving ? "Ukládám…" : "Dokončit návrat"}
              </button>
            )}
            {status === "done" && (
              <button className="cta quiet" onClick={() => history.replace(backUrl)}>Zpět na trasu</button>
            )}
          </div>
        </div>
      </IonContent>

      {scannerOpen && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </IonPage>
  );
}
